import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { specificationsApi, type Specification } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  Dialog,
  PageHeader,
  StatusBadge,
  SwitchField,
  TextAreaField,
  TextField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { currentLocale } from '../../i18n/index.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The specification register — decision D87.
 *
 * IT STARTS EMPTY. Which standards a laboratory works to, and which issuing
 * bodies it recognises, are that laboratory's own facts, and the product seeds
 * none. Choosing a specification on an assignment names what the work is done
 * to; it adds no field, limit or formula to anything.
 */
export function SpecificationsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Specification | 'new' | null>(null);
  const [code, setCode] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [issuingBody, setIssuingBody] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);

  const list = useQuery({
    queryKey: ['specifications', { page }],
    queryFn: () => specificationsApi.list({ page, includeInactive: 'true' }),
  });

  const open = (target: Specification | 'new') => {
    const item = target === 'new' ? null : target;
    setCode(item?.code ?? '');
    setNameAr(item?.nameAr ?? '');
    setNameEn(item?.nameEn ?? '');
    setIssuingBody(item?.issuingBody ?? '');
    setNotes(item?.notes ?? '');
    setIsActive(item?.isActive ?? true);
    setEditing(target);
  };

  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: code.trim(),
        nameAr: orNull(nameAr),
        nameEn: orNull(nameEn),
        issuingBody: orNull(issuingBody),
        notes: orNull(notes),
      };
      return editing === 'new' || editing === null
        ? specificationsApi.create(body)
        : specificationsApi.update(editing.id, { ...body, isActive });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['specifications'] });
      show({ tone: 'success', message: t('specifications.saved') });
      setEditing(null);
    },
  });

  const mayManage = can(PERMISSIONS.CATALOGUE_MANAGE);

  const columns: readonly DataColumn<Specification>[] = [
    {
      key: 'code',
      header: t('specifications.codeLabel'),
      render: (item) => (
        <div className="min-w-0">
          <span className="block font-medium text-default-900" dir="ltr">
            {item.code}
          </span>
          <span className="block truncate text-caption text-default-600">
            {(locale === 'ar' ? item.nameAr ?? item.nameEn : item.nameEn ?? item.nameAr) ?? ''}
          </span>
        </div>
      ),
    },
    {
      key: 'issuingBody',
      header: t('specifications.issuingBodyLabel'),
      render: (item) => (
        <span className="text-default-700">{item.issuingBody ?? t('common.notRecorded')}</span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (item) => (
        <StatusBadge
          label={item.isActive ? t('common.active') : t('common.inactive')}
          tone={item.isActive ? 'neutral' : 'warning'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (item) =>
        mayManage ? (
          <ActionButton priority="tertiary" size="sm" onPress={() => open(item)}>
            {t('common.edit')}
          </ActionButton>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('specifications.title')}
        subtitle={t('specifications.subtitle')}
        breadcrumbs={[{ label: t('nav.settings'), to: '/settings' }, { label: t('specifications.title') }]}
        actions={
          mayManage ? (
            <ActionButton priority="primary" onPress={() => open('new')}>
              {t('specifications.add')}
            </ActionButton>
          ) : null
        }
      />

      <Alert tone="info" title={t('specifications.noticeTitle')} description={t('specifications.noticeBody')} />

      <div className="mt-gutter">
        <RegisterPanel>
          <DataTable
            label={t('specifications.title')}
            columns={columns}
            rows={list.data?.data ?? []}
            rowKey={(item) => item.id}
            isLoading={list.isLoading}
            error={list.error}
            onRetry={() => void list.refetch()}
            emptyContent={t('specifications.noneYet')}
            page={page}
            totalPages={list.data?.meta.totalPages ?? 1}
            onPageChange={setPage}
          />
        </RegisterPanel>
      </div>

      <Dialog
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('specifications.add') : t('common.edit')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setEditing(null)} isDisabled={save.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={code.trim() === ''}
              isLoading={save.isPending}
              onPress={() => save.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <TextField
            label={t('specifications.codeLabel')}
            description={t('specifications.codeHint')}
            value={code}
            onChange={setCode}
            isRequired
          />
          <TextField label={t('specifications.nameArLabel')} value={nameAr} onChange={setNameAr} />
          <TextField label={t('specifications.nameEnLabel')} value={nameEn} onChange={setNameEn} />
          <TextField
            label={t('specifications.issuingBodyLabel')}
            description={t('specifications.issuingBodyHint')}
            value={issuingBody}
            onChange={setIssuingBody}
          />
          <TextAreaField label={t('specifications.notesLabel')} value={notes} onChange={setNotes} rows={2} />
          {editing !== 'new' && editing !== null ? (
            <SwitchField
              label={t('specifications.activeLabel')}
              description={t('specifications.activeHint')}
              value={isActive}
              onChange={setIsActive}
            />
          ) : null}
          {save.error ? <ErrorNotice error={save.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}
